import React, {useState} from "react";
import AppBar from "@material-ui/core/AppBar";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import Hidden from "@material-ui/core/Hidden";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import {makeStyles} from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import MenuIcon from "@material-ui/icons/Menu";
import Link from "next/link";
import {useRouter} from "next/router";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import Section from "./Section";
import ChooseThemeDialog from "./ChooseThemeDialog";
import {CharactersDrawerItem, CharactersNavButton, useLoadCharacters} from "./CharactersLink";
import {useAuth} from "./../util/auth";
import {useDarkMode} from "./../util/theme";

import LinearProgress from "@material-ui/core/LinearProgress";
import {dailyCredits, planName, planSummary} from "../util/plan";

// The top of the account menu: who's signed in, their plan, and how many of today's credits are left
function AccountHeader({user, classes}) {
    const perDay = dailyCredits(user);
    const left = typeof user.availableCredits === "number" ? Math.max(0, user.availableCredits) : null;
    const label = accountLabel(user);
    return (
        <div className={classes.menuHeader} onClick={(event) => event.stopPropagation()}>
            <div className={classes.menuName}>{label}</div>
            {user.email && user.email !== label && <div className={classes.menuEmail}>{user.email}</div>}
            <div className={classes.menuPlanRow}>
                <span className={classes.planBadge}>{planName(user)}</span>
                {left !== null && perDay > 0 && <span className={classes.menuCredits}>{left} of {perDay} credits today</span>}
            </div>
            {left !== null && perDay > 0 && (
                <LinearProgress variant="determinate" color="primary" className={classes.creditBar}
                                value={Math.min(100, (100 * left) / perDay)}/>
            )}
        </div>
    );
}

// Who's signed in, for the account menu: their name if they have one, else their email
function accountLabel(user) {
    return user.name || user.displayName || user.email || "Account";
}

const useStyles = makeStyles((theme) => ({
    appBar: {
        backgroundColor: "rgba(18, 11, 16, 0.82)",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backdropFilter: "blur(12px)",
    },
    toolbar: {
        minHeight: 64,
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(0, 2),
        [theme.breakpoints.down("xs")]: {
            minHeight: 56,
        },
    },
    brandLink: {
        display: "flex",
        alignItems: "center",
        textDecoration: "none",
        color: "inherit",
    },
    logo: {
        height: 26,
        width: "auto",
        marginRight: theme.spacing(1),
        // the quill is a black SVG; tint it to the accent
        filter: "invert(64%) sepia(38%) saturate(900%) hue-rotate(296deg) brightness(98%) contrast(92%)",
    },
    brandText: {
        fontFamily: theme.typography.h1.fontFamily,
        fontWeight: 600,
        letterSpacing: "-0.01em",
        fontSize: "1.3rem",
    },
    spacer: {
        flexGrow: 1,
    },
    navLinks: {
        display: "flex",
        alignItems: "center",
        marginRight: theme.spacing(1),
        "& > * + *": {
            marginLeft: theme.spacing(0.5),
        },
    },
    desktopActions: {
        display: "flex",
        alignItems: "center",
    },
    navButton: {
        fontWeight: 500,
        color: theme.palette.text.secondary,
        "&:hover": {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.action.hover,
        },
    },
    authGroup: {
        display: "flex",
        alignItems: "center",
        marginLeft: theme.spacing(1),
        "& > * + *": {
            marginLeft: theme.spacing(1),
        },
    },
    accountButton: {
        fontWeight: 500,
        textTransform: "none",
    },
    accountName: {
        maxWidth: 200,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    drawerAccount: {
        opacity: 0.7,
        overflowWrap: "anywhere",
    },
    menuHeader: {
        padding: theme.spacing(1.75, 2, 1.5),
        minWidth: 240,
        maxWidth: 280,
        outline: "none",
    },
    menuName: {
        fontWeight: 600,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    menuEmail: {
        fontSize: "0.8rem",
        color: theme.palette.text.secondary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    menuPlanRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.25),
    },
    planBadge: {
        padding: theme.spacing(0.25, 1),
        borderRadius: 999,
        fontSize: "0.72rem",
        fontWeight: 600,
        letterSpacing: "0.02em",
        color: theme.palette.primary.main,
        backgroundColor: "rgba(236, 122, 150, 0.14)",
    },
    menuCredits: {
        fontSize: "0.78rem",
        color: theme.palette.text.secondary,
    },
    creditBar: {
        marginTop: theme.spacing(0.75),
        height: 4,
        borderRadius: 2,
        backgroundColor: "rgba(255, 220, 232, 0.1)",
        "& .MuiLinearProgress-bar": {
            borderRadius: 2,
        },
    },
    buttonIcon: {
        marginLeft: theme.spacing(0.5),
        fontSize: 18,
    },
    menuButton: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(-1),
        color: theme.palette.text.primary,
    },
    drawerList: {
        width: 280,
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(2),
    },
    drawerActions: {
        display: "block",
        padding: theme.spacing(2, 2, 1.5),
    },
    drawerDivider: {
        margin: theme.spacing(1, 0),
    },
    drawerSignup: {
        padding: theme.spacing(1, 2),
    },
}));

const useNewStoryStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        alignItems: "center",
    },
    inline: {
        marginLeft: theme.spacing(4),
        "& > * + *": {
            marginLeft: theme.spacing(1),
        },
    },
    stacked: {
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
        "& > * + *": {
            marginTop: theme.spacing(1),
        },
    },
    button: {
        fontWeight: 600,
    },
    primaryButton: {},
    secondaryButton: {
        color: theme.palette.text.secondary,
        "&:hover": {
            color: theme.palette.text.primary,
        },
    },
}));

const home = "/";

function Navbar(props) {
    const classes = useStyles();
    const auth = useAuth();
    const darkMode = useDarkMode();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [menuState, setMenuState] = useState(null);
    useLoadCharacters();

    const logo = props.logoInverted && darkMode.value ? props.logoInverted : props.logo;

    const navLinks = [
        {label: "Pricing", href: "/pricing"},
        {label: "About", href: "/about"},
    ];

    const handleOpenMenu = (event, id) => {
        setMenuState({anchor: event.currentTarget, id});
    };

    const handleCloseMenu = () => {
        setMenuState(null);
    };

    const handleDrawerClose = () => setDrawerOpen(false);

    return (
        <Section bgColor={props.color} size="auto">
            <AppBar position="static" color="transparent" elevation={0} className={classes.appBar}>
                <Container maxWidth="lg">
                    <Toolbar disableGutters={true} className={classes.toolbar}>
                        <Link href={home} passHref>
                            <Box component="a" className={classes.brandLink}>
                                {logo && (
                                    <img src={logo} alt="Logo" className={classes.logo}/>
                                )}
                                <Typography variant="h6" className={classes.brandText}>
                                    MyTales
                                </Typography>
                            </Box>
                        </Link>

                        <Hidden smDown={true} implementation="css">
                            <NewStory/>
                        </Hidden>

                        <div className={classes.spacer}/>

        <Hidden smDown={true} implementation="css">
            <Box className={classes.desktopActions}>
                <Box className={classes.navLinks}>
                    {navLinks.map((item) => (
                        <Link key={item.href} href={item.href} passHref>
                            <Button component="a" color="inherit" className={classes.navButton}>
                                {item.label}
                            </Button>
                        </Link>
                    ))}
                </Box>

                {!auth.user && (
                    <Box className={classes.authGroup}>
                        <Link href="/auth/signin" passHref>
                            <Button component="a" color="inherit" className={classes.navButton}>
                                Sign in
                            </Button>
                        </Link>
                        <Link href="/auth/signup" passHref>
                            <Button component="a" variant="contained" color="primary">
                                Sign up
                            </Button>
                        </Link>
                    </Box>
                )}

                {auth.user && (
                    <>
                        <Button
                            className={`${classes.navButton} ${classes.accountButton}`}
                            color="inherit"
                            aria-label="Account"
                            aria-controls="account-menu"
                            aria-haspopup="true"
                            onClick={(event) => handleOpenMenu(event, "account-menu")}
                        >
                            <span className={classes.accountName}>{accountLabel(auth.user)}</span>
                            <ExpandMoreIcon className={classes.buttonIcon}/>
                        </Button>
                        <Menu
                            id="account-menu"
                            autoFocus={false}
                            variant="menu"
                            open={Boolean(menuState && menuState.id === "account-menu")}
                            anchorEl={menuState && menuState.anchor}
                            getContentAnchorEl={null}
                            onClick={handleCloseMenu}
                            onClose={handleCloseMenu}
                            keepMounted={true}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "center",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "center",
                            }}
                        >
                            <AccountHeader user={auth.user} classes={classes}/>
                            <Divider/>
                            <Link href="/settings/general" passHref>
                                <MenuItem component="a">Settings</MenuItem>
                            </Link>
                            <Divider/>
                            <MenuItem
                                onClick={() => auth.signout()}
                            >
                                Sign out
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </Box>
        </Hidden>

                        <Hidden mdUp={true} implementation="css">
                            <IconButton
                                onClick={() => {
                                    setDrawerOpen(true);
                                }}
                                color="inherit"
                                className={classes.menuButton}
                            >
                                <MenuIcon/>
                            </IconButton>
                        </Hidden>
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleDrawerClose}
                ModalProps={{keepMounted: true}} // keep dialog state when drawer closes
            >
                <List className={classes.drawerList} onClick={handleDrawerClose}>
                    <ListItem className={classes.drawerActions} disableGutters>
                        <NewStory layout="stacked" afterAction={handleDrawerClose}/>
                    </ListItem>

                    <Divider className={classes.drawerDivider}/>

                    <Link href={home} passHref>
                        <ListItem component="a" button={true}>
                            <ListItemText primary="Home"/>
                        </ListItem>
                    </Link>

                    {navLinks.map((item) => (
                        <Link key={item.href} href={item.href} passHref>
                            <ListItem component="a" button={true}>
                                <ListItemText primary={item.label}/>
                            </ListItem>
                        </Link>
                    ))}

                    <Divider className={classes.drawerDivider}/>

                    {!auth.user && (
                        <>
                            <Link href="/auth/signin" passHref>
                                <ListItem component="a" button={true}>
                                    <ListItemText primary="Sign in"/>
                                </ListItem>
                            </Link>
                            <Link href="/auth/signup" passHref>
                                <ListItem className={classes.drawerSignup}>
                                    <Button
                                        component="a"
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                    >
                                        Sign up
                                    </Button>
                                </ListItem>
                            </Link>
                        </>
                    )}

                    {auth.user && (
                        <>
                            <ListItem>
                                <ListItemText primary={accountLabel(auth.user)} secondary={planSummary(auth.user)}
                                              className={classes.drawerAccount}/>
                            </ListItem>
                            <Link href="/settings/general" passHref>
                                <ListItem component="a" button={true}>
                                    <ListItemText primary="Settings"/>
                                </ListItem>
                            </Link>
                            <Link href="/your-stories" passHref>
                                <ListItem component="a" button={true}>
                                    <ListItemText primary="Stories"/>
                                </ListItem>
                            </Link>
                            <CharactersDrawerItem/>
                            <Divider className={classes.drawerDivider}/>
                            <ListItem
                                button={true}
                                onClick={() => auth.signout()}
                            >
                                <ListItemText primary="Sign out"/>
                            </ListItem>
                        </>
                    )}
                </List>
            </Drawer>
        </Section>
    );
}

export default Navbar;

function NewStory({layout = "inline", afterAction}) {
    const classes = useNewStoryStyles();
    const auth = useAuth();
    const [open, setOpen] = useState(false);
    const router = useRouter();

    const rootClassName = `${classes.root} ${layout === "stacked" ? classes.stacked : classes.inline}`;

    const handleOpen = (event) => {
        if (event && event.stopPropagation) {
            event.stopPropagation();
        }
        setTimeout(() => setOpen(true), 0);
        if (afterAction) {
            afterAction();
        }
    };

    const handleBrowse = (event) => {
        if (event && event.stopPropagation) {
            event.stopPropagation();
        }
        if (afterAction) {
            afterAction();
        }
        router.push("/browse");
    };

    return (
        <>
            <ChooseThemeDialog
                open={open}
                onClose={(selected) => {
                    setOpen(false);
                    if (selected) {
                        const normalizedString = selected;
                        window.location.href = `/theme/${normalizedString}`;
                    }
                }}
            />
            <Box className={rootClassName}>
                <Button
                    variant="contained"
                    color="primary"
                    className={`${classes.button} ${classes.primaryButton}`}
                    onClick={handleOpen}
                    fullWidth={layout === "stacked"}
                >
                    New story
                </Button>
                {auth.user && layout !== "stacked" && (
                    <>
                        <Link href="/your-stories" passHref>
                            <Button component="a" className={`${classes.button} ${classes.secondaryButton}`}>
                                Stories
                            </Button>
                        </Link>
                        <CharactersNavButton className={`${classes.button} ${classes.secondaryButton}`}/>
                    </>
                )}
                <Button
                    variant={layout === "stacked" ? "outlined" : "text"}
                    className={`${classes.button} ${classes.secondaryButton}`}
                    onClick={handleBrowse}
                    fullWidth={layout === "stacked"}
                >
                    Browse
                </Button>
            </Box>
        </>
    );
}
